/**
 * Driving the gap census to completion, one bounded request at a time.
 *
 * Same shape and same reason as `reconcileRun`: opening every stored source of
 * every deal is minutes of work on one CPU, no single request can hold it, and
 * a request the platform's ingress kills carries no headers at all — so a
 * timeout reaches the browser wearing a CORS error's clothes. The server
 * answers within a time budget and names the records it never started; this
 * hands those back until the run is complete.
 *
 * It terminates by construction: every call starts at least one deal, so the
 * not-started set strictly shrinks. `MAX_ROUNDS` guards a server that stops
 * honouring that, and reaching it returns what was done with the rest still
 * named rather than spinning.
 */
import type { GapCensusDesk, GapResolution } from '@/services/ingestion'

/** Far above any real desk. */
export const MAX_ROUNDS = 50

/** Two batches of one run, read as one. */
export function mergeCensusBatches(first: GapCensusDesk, next: GapCensusDesk): GapCensusDesk {
  const totals: Partial<Record<GapResolution, number>> = { ...first.totals }
  for (const [key, value] of Object.entries(next.totals)) {
    const k = key as GapResolution
    totals[k] = (totals[k] ?? 0) + (value ?? 0)
  }
  const skipped: Record<string, string[]> = { ...first.skipped }
  for (const [key, records] of Object.entries(next.skipped)) {
    skipped[key] = [...(skipped[key] ?? []), ...records]
  }
  // One deal, one entry. Two intake records can convert into one deal — a
  // thread, a broker re-sending with the deck attached — and the server sees
  // only the records inside its own batch.
  const seen = new Set(first.deals.map((d) => d.loanId ?? d.ingestionId))
  return {
    ...next,
    deals: [...first.deals, ...next.deals.filter((d) => !seen.has(d.loanId ?? d.ingestionId))],
    skipped,
    totals,
    populated: first.populated + next.populated,
    catalogue: first.catalogue + next.catalogue,
    engineMisses: first.engineMisses + next.engineMisses,
    elapsedSeconds: Number(((first.elapsedSeconds ?? 0) + (next.elapsedSeconds ?? 0)).toFixed(2)),
  }
}

export type CensusProgress = {
  /** Deals censused so far, across every batch. */
  done: number
  /** Records the last batch had not started. */
  remaining: number
  /** Which request this is, from 1. */
  round: number
}

type Options = {
  /** One bounded call. Injected so the loop is testable without a transport. */
  call: (input: { ids?: string[]; search: boolean }) => Promise<GapCensusDesk>
  /** False answers "what is empty" without opening a document — fast, and
   *  honest about not having looked. */
  search?: boolean
  onProgress?: (progress: CensusProgress) => void
  /** Restrict the whole run to these records; batching still applies within it. */
  ids?: string[]
}

export async function runGapCensus({
  call,
  search = true,
  onProgress,
  ids,
}: Options): Promise<GapCensusDesk> {
  let accumulated: GapCensusDesk | null = null
  let pending: string[] | undefined = ids

  for (let round = 1; round <= MAX_ROUNDS; round += 1) {
    const batch = await call({ ids: pending, search })
    accumulated = accumulated ? mergeCensusBatches(accumulated, batch) : batch
    onProgress?.({
      done: accumulated.deals.length,
      remaining: batch.remaining?.length ?? 0,
      round,
    })
    if (batch.complete || !batch.remaining?.length) return accumulated
    pending = batch.remaining
  }
  return accumulated as GapCensusDesk
}
